import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  companies,
  locations,
  incomeRanges,
  occupations,
} from "../../data/demoForm";
import Swal from "sweetalert2";
import axios from "axios";
import { url } from "../../data/url";

const BackArrowIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19 12H5M5 12L12 19M5 12L12 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StudentForm = ({
  onCompanySelected,
  toggleFilters,
  isVisible,
  toggleStep,
  currentStep,
  selectedCompany,
  toggleSelectCompany,
}) => {
  const navigate = useNavigate();

  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [industry, setIndustry] = useState("");
  const [occupation, setOccupation] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState([]);

  // Get unique industries
  const industries = [...new Set(companies.map((company) => company.industry))];
  
  // Get unique states
  const states = [...new Set(companies.map((company) => company.state))].sort();

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${url.path}/user/select-company`, { withCredentials: true });
      if(response.data && response.data.companySelected != null) {
        setState(response.data.state || "");
        setZipCode(response.data.zipCode || "");
        setIndustry(response.data.industry || "");
        setOccupation(response.data.occupation || "");
      }
    } catch (error) {
      if(error.response && error.response.status === 401) {
        navigate('/signin');
      }
      console.error('Error fetching user data:', error);
    }
  };

  useEffect(() => {
    const filtered = companies.filter(
      (company) =>
        (!state || company.state === state) &&
        (!zipCode || company.zipCode === zipCode) &&
        (!industry || company.industry === industry)
    );
    setFilteredCompanies(filtered.length > 0 ? filtered : []);
  }, [state, zipCode, industry]);

  const handleZipCodeChange = (e) => {
    // Only allow numbers and limit to 5 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(value);
  };

  const handleNext = () => {
    if (currentStep < 2) {
      toggleStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      toggleStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const selected = document.querySelector('input[name="company"]:checked');
    if (selected) {
      const selectedCompany = selected.value;
      toggleSelectCompany(selectedCompany);

      try {
        const data = {
          companySelected: selectedCompany,
          state: state,
          zipCode: zipCode,
          industry: industry,
          occupation: occupation,
        };
  
        const response = await axios.post(
          `${url.path}/user/select-company`,
          data,
          {
            withCredentials: true,
          }
        );

        if (response.status === 200) {
          onCompanySelected();
          if (isVisible && isVisible === true) {
            toggleFilters();
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          navigate("/signin");
        } else {
          console.error("Error updating company selection:", error);
          Swal.fire({
            title: "Error!",
            text: "There was an error updating your company selection. Please try again.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    } else {
      Swal.fire({
        title: "No Company Selected",
        text: "Please select a company to continue.",
        icon: "warning",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <div className="demo-form">
      <main className="demo-form-container">
        {currentStep === 2 && (
          <div className="demo-form-prev-btn">
            <button onClick={handlePrev} className="prev-button">
              <BackArrowIcon />
            </button>
          </div>
        )}
        <section className="form-section">
          <h2>Welcome to AMIE</h2>
          <p className="instruction">
            Filter below for an industry & employer of interest to support your
            training:
          </p>
          <form id="demo-form" onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <div className="form-step active">
                <label htmlFor="industry">Filter by Industry:</label>
                <select
                  id="industry"
                  name="industry"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                >
                  <option value="">Select an industry</option>
                  {industries.map((ind, index) => (
                    <option key={index} value={ind}>
                      {ind}
                    </option>
                  ))}
                </select>

                <label htmlFor="occupation">Filter by Occupation:</label>
                <select
                  id="occupation"
                  name="occupation"
                  value={occupation}
                  onChange={(e) => setOccupation(e.target.value)}
                >
                  <option value="">Select an occupation</option>
                  {occupations.map((ind, index) => (
                    <option key={index} value={ind.name}>
                      {ind.name}
                    </option>
                  ))}
                </select>

                <label htmlFor="state">Filter by State:</label>
                <select
                  id="state"
                  name="state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                >
                  <option value="">Select a state</option>
                  {states.map((state, index) => (
                    <option key={index} value={state}>
                      {state}
                    </option>
                  ))}
                </select>

                <label htmlFor="zipCode">Enter Zip Code:</label>
                <input
                  type="text"
                  id="zipCode"
                  name="zipCode"
                  value={zipCode}
                  onChange={handleZipCodeChange}
                  placeholder="Enter 5-digit zip code"
                  maxLength="5"
                  pattern="\d{5}"
                />

                <div className="form-navigation-first">
                  <button
                    type="button"
                    className="next-button"
                    onClick={handleNext}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="form-step">
                <h3>Select a Company:</h3>
                <div id="company-list" className="company-list">
                  {filteredCompanies.length > 0 ? (
                    filteredCompanies.map((company, index) => (
                      <label key={index} className="company-option">
                        <input
                          type="radio"
                          name="company"
                          value={company.name}
                          checked={selectedCompany === company.name}
                          onChange={() => toggleSelectCompany(company.name)}
                        />
                        <span className="company-name">{company.name}</span>
                      </label>
                    ))
                  ) : (
                    <p>No company to show</p>
                  )}
                </div>

                <div className="form-navigation">
                  <button type="submit" className="submit-button">
                    Submit
                  </button>
                </div>
              </div>
            )}
          </form>
        </section>
      </main>
    </div>
  );
};

export default StudentForm;